"use client";

import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Chat, mapHistoryToMessages } from "@/components/chat";
import { EvaluationPanel } from "@/components/evaluation-panel";
import { IntentPanel } from "@/components/intent-panel";
import { OutcomePanel } from "@/components/outcome-panel";
import {
  classifyOutcome,
  evaluateSession,
  getSessionDetail,
  getTagSet,
  retagSession,
  sessionAudioUrl,
  sessionRecordingUrl,
  updateSessionTags,
  type SessionDetail as SessionDetailData,
} from "@/lib/api";
import { formatDuration, formatTime, SOURCE_LABELS } from "@/lib/utils";

function parseTags(raw: string | null): { status: "not_run" | "empty" | "has_tags"; tags: string[] } {
  if (raw === null || raw === undefined) return { status: "not_run", tags: [] };
  const parsed: string[] = JSON.parse(raw);
  return parsed.length > 0 ? { status: "has_tags", tags: parsed } : { status: "empty", tags: [] };
}

export function SessionDetail({ sessionId, readOnly }: { sessionId: string; readOnly?: boolean }) {
  const [detail, setDetail] = useState<SessionDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [evalLoading, setEvalLoading] = useState(false);
  const [outcomeLoading, setOutcomeLoading] = useState(false);
  const [tagLoading, setTagLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [editingTags, setEditingTags] = useState(false);
  const [draftTags, setDraftTags] = useState<string[]>([]);

  useEffect(() => {
    getSessionDetail(sessionId).then((d) => {
      setDetail(d);
      setLoading(false);
      if (d.playbook_id) {
        getTagSet(d.playbook_id).then((ts) => setAvailableTags(ts.tags || []));
      }
    });
  }, [sessionId]);

  const handleReEvaluate = () => {
    setEvalLoading(true);
    evaluateSession(sessionId)
      .then((ev) => setDetail((d) => d ? { ...d, evaluation: ev } : d))
      .finally(() => setEvalLoading(false));
  };

  const handleRetag = () => {
    setTagLoading(true);
    retagSession(sessionId)
      .then((result) => setDetail((d) => d ? { ...d, tags: JSON.stringify(result.tags) } : d))
      .finally(() => setTagLoading(false));
  };

  const handleClassifyOutcome = () => {
    setOutcomeLoading(true);
    classifyOutcome(sessionId)
      .then((result) => setDetail((d) => d ? {
        ...d,
        success: result.success ? 1 : 0,
        reject_reason: result.reject_reason,
      } : d))
      .finally(() => setOutcomeLoading(false));
  };

  if (loading || !detail) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        加载中...
      </div>
    );
  }

  const messages = mapHistoryToMessages(
    detail.history,
    detail.session_id,
    detail.has_audio,
    sessionAudioUrl,
  );

  const tagInfo = parseTags(detail.tags);

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
        <span className="font-mono text-xs text-muted-foreground">{detail.session_id.slice(0, 8)}</span>
        <span title={SOURCE_LABELS[detail.source] ?? detail.source}>{SOURCE_LABELS[detail.source] ?? `❓${detail.source}`}</span>
        <span className="text-xs text-muted-foreground">{detail.playbook_id}</span>
        {detail.phone && (
          <span className="font-mono text-xs text-muted-foreground">{detail.phone}</span>
        )}
        <span className="text-xs text-muted-foreground">{formatTime(detail.created_at)}</span>
        <span className="text-xs text-muted-foreground">{detail.turn_count} 轮</span>
        <span className="text-xs text-muted-foreground">{formatDuration(detail.duration_ms)}</span>
        {detail.ended_at ? (
          <Badge variant="secondary">已结束</Badge>
        ) : (
          <Badge variant="outline">进行中</Badge>
        )}
        {detail.hangup_by && (
          <span className="text-xs text-muted-foreground">
            {detail.hangup_by === "nord" ? "AI 挂断" : "对方挂断"}
          </span>
        )}
        {detail.amd_detected === 1 && (
          <Badge variant="outline" className="bg-amber-100 text-amber-800">语音信箱</Badge>
        )}
      </div>
      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-hidden">
          <Chat
            messages={messages}
            ended={true}
            loading={false}
            onSend={() => {}}
            sessionId={detail.session_id}
            recordingUrl={detail.has_audio ? sessionRecordingUrl(detail.session_id) : undefined}
            userRecordingUrl={detail.has_audio ? sessionRecordingUrl(detail.session_id, "user") : undefined}
            aiRecordingUrl={detail.has_audio ? sessionRecordingUrl(detail.session_id, "ai") : undefined}
            readOnly={readOnly}
          />
        </div>
        <div className="w-80 border-l overflow-auto p-4 space-y-4">
          {detail.turn_count === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <p className="text-sm font-medium text-muted-foreground">无有效对话</p>
              <p className="text-xs text-muted-foreground mt-1">未检测到用户回复，跳过通后分析</p>
            </div>
          ) : (
            <>
              <IntentPanel
                sessionId={sessionId}
                intentScore={detail.intent_score}
                intentDirectionsJson={detail.intent_directions}
                intentAt={detail.intent_at}
                onUpdate={(r) => setDetail((d) => d ? {
                  ...d,
                  intent_score: r.intent_score,
                  intent_directions: JSON.stringify(r.intent_directions),
                  intent_at: r.intent_score != null ? Date.now() / 1000 : d.intent_at,
                } : d)}
              />

              <hr />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-medium">标签</h3>
                  <div className="flex gap-1">
                    {editingTags ? (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setEditingTags(false)} disabled={saveLoading}>
                          取消
                        </Button>
                        <Button size="sm" disabled={saveLoading} onClick={() => {
                          setSaveLoading(true);
                          updateSessionTags(sessionId, draftTags)
                            .then((resp) => {
                              setDetail((d) => d ? { ...d, tags: JSON.stringify(resp.tags) } : d);
                              setEditingTags(false);
                            })
                            .finally(() => setSaveLoading(false));
                        }}>
                          {saveLoading ? "保存中..." : "保存"}
                        </Button>
                      </>
                    ) : (
                      <>
                        {availableTags.length > 0 && (
                          <Button variant="outline" size="sm" onClick={() => {
                            setDraftTags(tagInfo.tags);
                            setEditingTags(true);
                          }}>
                            编辑
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={handleRetag} disabled={tagLoading}>
                          {tagLoading ? "标注中..." : tagInfo.status === "not_run" ? "标注" : "重新标注"}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                {editingTags ? (
                  <div className="flex flex-wrap gap-1">
                    {draftTags
                      .filter((tag) => !availableTags.includes(tag))
                      .map((tag) => (
                        <Badge
                          key={`invalid-${tag}`}
                          variant="destructive"
                          className="text-xs cursor-pointer"
                          title="非法标签，点击移除"
                          onClick={() => setDraftTags(draftTags.filter((t) => t !== tag))}
                        >
                          {tag} ✕
                        </Badge>
                      ))}
                    {availableTags.map((tag) => {
                      const selected = draftTags.includes(tag);
                      return (
                        <Badge
                          key={tag}
                          variant={selected ? "default" : "outline"}
                          className={`text-xs cursor-pointer ${selected ? "" : "opacity-40"}`}
                          onClick={() => {
                            setDraftTags(selected
                              ? draftTags.filter((t) => t !== tag)
                              : [...draftTags, tag]);
                          }}
                        >
                          {tag}
                        </Badge>
                      );
                    })}
                  </div>
                ) : tagInfo.status === "not_run" ? (
                  <p className="text-sm text-muted-foreground">未执行</p>
                ) : tagInfo.status === "empty" ? (
                  <p className="text-sm text-muted-foreground">无标签</p>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {tagInfo.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                )}
              </div>

              <hr />

              <OutcomePanel
                success={detail.success}
                rejectReason={detail.reject_reason}
                onClassify={handleClassifyOutcome}
                loading={outcomeLoading}
              />

              <hr />

              <EvaluationPanel
                evaluation={detail.evaluation}
                onReEvaluate={handleReEvaluate}
                loading={evalLoading}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
